import service from "Utility/Service";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { AxiosRequestConfig } from "axios";
import { DevicesStatus, DevicesIdentity, Statistic } from "Utility/Types/status_identity_get_set";
import { FcCdLogo } from "react-icons/fc";

const phoneNumber = (simNumber: any) => {
  let phone_number;
  if (!simNumber) {
    return "";
  } else if (Array.isArray(simNumber)) {
    // 若有多個電話號碼，返回時由 "/" 分隔電話號碼的字串
    phone_number = simNumber
      .filter((item) => item.phone_number.length)
      .map((item) => item.phone_number)
      .join("/");
  } else {
    phone_number = simNumber?.phone_number ?? "";
  }

  return phone_number;
};

/* 根據提供的 level、stateCid 和 loginCid 檢索設備狀態信息。
   使用Post 請求 到 "/cmd" 端點過濾器，過濾條件基於用戶級別、狀態Cid、登入Cid。
   返回 DevicesStatus 介面定義好的資料型態
*/
const getStatusByCid = async (level: any, stateCid: any, loginCid: any): Promise<DevicesStatus> => {
  let body: any = { get: { device_status: { filter: {}, nodeInf: {}, obj: {} } } };

  if (level === "super_super" && stateCid !== "") {
    body.get.device_status.filter.cid = stateCid;
  } else if (level !== "super_super") {
    body.get.device_status.filter.cid = loginCid;
  }

  const config: AxiosRequestConfig = {
    method: "post",
    url: "/cmd",
    data: body,
  };

  const { data } = await service(config);

  if (data.response?.device_status) {
    let groups: any = new Set();
    let models: any = new Set();
    let locations: any = new Set();
    let devicesIDorName: any = new Set();
    let devicesID: any = new Set();
    let descriptions: any = new Set();

    let tableData = data.response.device_status.map((item: any, index: number) => {
      if (item.nodeInf.gid?.length) {
        item.nodeInf.gid.forEach((item: string) => groups.add(item));
      }
      models.add(item.nodeInf.model);
      if (item.nodeInf.city) {
        locations.add(item.nodeInf.city);
      }
      if (item.nodeInf.description) {
        descriptions.add(item.nodeInf.description);
      }
      devicesIDorName.add(item.nodeInf.name !== "" ? item.nodeInf.name : item.nodeInf.id);
      devicesID.add(item.nodeInf.id);

      return {
        location: item.nodeInf.city || undefined,
        description:item.nodeInf.description|| undefined,
        city: item.nodeInf.city || undefined, // duplicated
        key: index,
        id: item.nodeInf.id,
        name: item.nodeInf.name,
        name_or_id:
          item.nodeInf.name === "" || item.nodeInf.name === null || item.nodeInf.name === undefined
            ? item.nodeInf.id
            : item.nodeInf.name,
        health: item.nodeInf.health,
        model: item.nodeInf.model,
        lastupdate: item.nodeInf.lastUpdate,
        strength: item.nodeInf.sim,
        connection: item.nodeInf.connect,
        cid: item.nodeInf.cid,
        groups: item.nodeInf.gid, // duplicated
        gid: item.nodeInf.gid,
        sim_active: item.obj?.status?.sim?.[0]?.active || item.obj?.status?.sim?.[1]?.active,
        latitude: item.obj?.status?.gps?.latitude || 0,
        latest_connection_time:
          item.nodeInf?.connect === false ? item.nodeInf?.sub_utc : item.nodeInf?.lastUpdate - item.nodeInf?.sub_utc,
        longitude: item.obj?.status?.gps?.longitude || 0,
        phone_number_status: phoneNumber(item.obj?.status?.sim),
        phone_number_nodeInf: item.nodeInf.phone_number || "",
        lati_longi: !item.nodeInf.lati_longi || item.nodeInf.lati_longi === "" ? [0, 0] : item.nodeInf.lati_longi,
        sub_utc: item.nodeInf?.sub_utc,
      };
    });
    data.response.tableData = tableData;
    data.response.models = Array.from(models);
    data.response.groups = Array.from(groups);
    data.response.locations = Array.from(locations);
    data.response.devicesIDorName = Array.from(devicesIDorName);
    data.response.devicesID = Array.from(devicesID);
    data.response.descriptions = Array.from(descriptions);

  }
  return data;
};
/* 利用 React Query 的 useQuery Hook 使用 getStatusByCid 函數獲取設備狀態數據。
   接受 level、stateCid、loginCid 和 isLogin 作為參數。
   只有在 isLogin 為 true 時啟用，提供基於查詢鍵更改的緩存和自動數據獲取。*/
export const useDeviceStatus = (level: string, stateCid: string, loginCid: string, isLogin: boolean = true) => {
  return useQuery(["status", stateCid], () => getStatusByCid(level, stateCid, loginCid), {
    enabled: isLogin,
  });
};

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/*根據提供的 deviceID 檢索設備狀態信息。
  使用 POST 請求構建到 "/cmd" 端點的過濾器，過濾條件基於設備 ID。
  返回包含設備狀態信息的對象。*/
const getStatusByID = async (deviceID: string | undefined) => {
  const config: AxiosRequestConfig = {
    method: "post",
    url: "/cmd",
    data: {
      get: {
        device_status: {
          filter: { id: deviceID },
          nodeInf: {},
          obj: {},
        },
      },
    },
  };

  const { data }: { data: DevicesStatus } = await service(config);
  return data.response?.device_status?.[0]?.obj;
};

export const useDeviceStatusByID = (deviceID: string | undefined, deviceStateVisible: boolean) => {
  return useQuery(["statusByID", deviceID], () => getStatusByID(deviceID), {
    // !! 布林轉換運算符，可將任何值轉為布林值，如果 deviceID 未定義 "!!deviceID 為false" 反之為 true
    // enabled：啟用查詢請求   *預設為true 自動查詢請求*  false為停止查詢請求
    enabled: !!deviceID && deviceStateVisible,
  });
};

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface phone_number_props {
  deviceID: string;
  phone_number: number | undefined | "";
}

const updateStatusByID = async (props: phone_number_props) => {
  // 從傳入的 props 對象中提取 deviceID 和 phone_number
  const { deviceID, phone_number } = props;
  // 如果 phone_number 為空，則構建一個配置對象，使用 HTTP PATCH 方法向指定 URL 發送請求
  if (phone_number === "") {
    const config: AxiosRequestConfig = {
      method: "patch",
      url: `api/devicestatus/${deviceID}/`,    // 使用 deviceID 動態生成 URL
      data: { phone_number: "0000000000000" }, // 設置要更新的狀態數據 若等於 "" 給他預設值
    };
    return service(config);
  } else {
    // 如果 phone_number 不為空，則使用類似的方式構建和發送請求，但使用提供的 phone_number 值
    const config: AxiosRequestConfig = {
      method: "patch",
      url: `api/devicestatus/${deviceID}/`,     // 使用 deviceID 動態生成 URL
      data: { phone_number: phone_number },     // 設置要更新的狀態數據
    };
    return service(config);
  }
};

export const useUpdateStatus = (stateCid: string) => {
  const queryClient = useQueryClient();
  // 在這個回調函數中，調用 updateStatusByID 函數來執行狀態更新操作
  // props 包含更新所需的信息
  return useMutation((props: phone_number_props) => updateStatusByID(props), {
    // 在成功完成狀態更新操作後，執行以下操作
    // 使用 queryClient.invalidateQueries 方法，標記名稱為 ["status", stateCid] 的查詢為失效
    // 這將觸發 React Query 重新拉取這個查詢的最新數據
    onSuccess: () => queryClient.invalidateQueries(["status", stateCid]),
  });
};

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/*根據提供的 deviceID 檢索設備狀態信息。
  使用 POST 請求構建到 "/cmd" 端點的過濾器，過濾條件基於設備 ID。
  返回包含設備狀態信息的對象。*/
const getIdentityByID = async (deviceID: string | undefined) => {
  const config: AxiosRequestConfig = {
    method: "post",
    url: "/cmd",
    data: {
      get: {
        device_identity: {
          filter: { id: deviceID },
          nodeInf: {},
          obj: {},
        },
      },
    },
  };

  const { data }: { data: DevicesIdentity } = await service(config);

  return data.response?.device_identity?.[0]?.obj?.identity;
};

export const useDeviceIdentityByID = (deviceID: string | undefined, deviceStateVisible: boolean) => {
  return useQuery(["identity", deviceID], () => getIdentityByID(deviceID), {
    enabled: !!deviceID && deviceStateVisible,
  });
};

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/* 根據提供的 level、stateCid 和 loginCid 檢索統計訊息。
   使用 POST 請求構建到 "/cmd" 端點的過濾器，過濾條件基於用戶級別和 CIDs。
   ( 返回 health 與 sim 數據設備狀態統計訊息 )
*/

const getStatisticByCid = async (level: any, stateCid: any, loginCid: any) => {
  let body: any = { get: { statistic: { filter: {} } } };

  if (level === "super_super" && stateCid !== "") {
    body.get.statistic.filter.cid = stateCid;
  } else if (level !== "super_super") {
    body.get.statistic.filter.cid = loginCid;
  }
  const config: AxiosRequestConfig = {
    method: "post",
    url: "/cmd",
    data: body,
  };

  const { data }: { data: Statistic } = await service(config);
  return data.response?.statistic?.obj;
};

/*  利用 useQuery Hook 使用 getStatisticByCid 函數獲取統計數據。
    接受 level、stateCid、loginCid 和 isLogin 作為參數。
    只有在 isLogin 為 true 時啟用。
*/
export const useStatistic = (level: string, stateCid: string, loginCid: string, isLogin: boolean = true) => {
  return useQuery(["statistic", stateCid], () => getStatisticByCid(level, stateCid, loginCid), {
    enabled: isLogin,
  });
};

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/* 根據提供的 level、stateCid 和 loginCid 檢索 IoT 設備信息。
   使用 POST 請求構建到 "/cmd" 端點的過濾器，過濾條件基於用戶級別和 CIDs。
   返回響應對象。
*/
const getDeviceIot = async (level: any, stateCid: any, loginCid: any) => {
  let body: any = { get: { device_iot: { filter: {} } } };

  if (level === "super_super" && stateCid !== "") {
    body.get.device_iot.filter.cid = stateCid;
  } else if (level !== "super_super") {
    body.get.device_iot.filter.cid = loginCid;
  }

  const config: AxiosRequestConfig = {
    method: "post",
    url: "/cmd",
    data: body,
  };

  const { data } = await service(config);
  return data.response;
};

export const useDeviceIot = (level: string, stateCid: string, loginCid: string, isLogin: boolean = true) => {
  return useQuery(["get-device-iot", stateCid], () => getDeviceIot(level, stateCid, loginCid), {
    enabled: isLogin,
    refetchInterval: 3000, // 每 5 秒更新一次
  });
};
